import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Armchair, MapPin, UsersRound } from "lucide-react";
import Axios from "../../services/AxiosInterceptor";
import HeroWithServicesInfo from "./HerowithInstruction";

const itemsPerPage = 6;
const STORAGE_URL = "https://yogamatic.in/api/";

const GetStart = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await Axios.get("/api/yoga_services");
        const data = response.data;
        if (data.success) {
          setServices(data.data || []);
        } else {
          setError("Failed to fetch services");
        }
      } catch (err) {
        setError("Something went wrong while loading services.");
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const visibleServices = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return services.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, services]);

  const groupedServices = useMemo(() => {
    return visibleServices.reduce((grouped, service) => {
      (service.locations || []).forEach((location) => {
        if (!grouped[location.name]) grouped[location.name] = [];
        grouped[location.name].push(service);
      });
      return grouped;
    }, {});
  }, [visibleServices]);

  const totalPages = Math.ceil(services.length / itemsPerPage);

  const ServiceCard = ({ service, locationName, index }) => (
    <motion.article
      className="ym-service-card"
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ delay: (index % 3) * 0.06 }}
      whileHover={{ y: -6 }}
    >
      <div className="ym-service-card-image">
        <img src={`${STORAGE_URL}upload/${service.image}`} alt={service.title} />
        <span className="ym-pill" style={{ position: "absolute", top: 12, left: 12, background: "rgba(255,255,255,0.9)" }}>
          {service.is_subscription ? "Subscription" : "Daily Session"}
        </span>
      </div>

      <div className="ym-service-card-body">
        <div>
          <h3 className="ym-service-title">{service.title}</h3>
          <div className="ym-check-list" style={{ marginTop: 12 }}>
            <div style={{ display: "flex", gap: 9, color: "var(--ym-muted)" }}>
              <UsersRound size={16} style={{ flex: "0 0 auto", marginTop: 3 }} />
              <span>{service.classMode || "Yoga class"} / {service.sessionType || "Session"}</span>
            </div>
            <div style={{ display: "flex", gap: 9, color: "var(--ym-muted)" }}>
              <Armchair size={16} style={{ flex: "0 0 auto", marginTop: 3 }} />
              <span>{service.seatAvailability || "Limited"} seats available</span>
            </div>
            <div style={{ display: "flex", gap: 9, color: "var(--ym-muted)" }}>
              <MapPin size={16} style={{ flex: "0 0 auto", marginTop: 3 }} />
              <span>{locationName}</span>
            </div>
          </div>
        </div>

        <button
          className="ym-button ym-button-primary"
          onClick={() => navigate(`/service_details/${service._id}/${locationName}`)}
        >
          View Details
          <ArrowRight size={17} />
        </button>
      </div>
    </motion.article>
  );

  return (
    <main className="ym-page">
      <HeroWithServicesInfo />

      <section className="ym-section">
        <div className="ym-container">
          <div style={{ textAlign: "center", maxWidth: 760, margin: "0 auto 38px" }}>
            <span className="ym-eyebrow" style={{ marginInline: "auto" }}>Available classes</span>
            <h2 className="ym-title-small">Book a session by location.</h2>
            <p className="ym-lead" style={{ marginInline: "auto" }}>
              Select a service, then choose location, resource, date, slot, and seats on the detail page.
            </p>
          </div>

          {loading && <div className="ym-loader">Loading services...</div>}
          {error && !loading && <div className="ym-empty">{error}</div>}

          {!loading && !error && Object.keys(groupedServices).length === 0 && (
            <div className="ym-empty">No yoga services are available right now.</div>
          )}

          {!loading && !error && Object.entries(groupedServices).map(([locationName, locationServices]) => (
            <section key={locationName} style={{ marginTop: 42 }}>
              <div className="ym-meta-row" style={{ justifyContent: "space-between", marginBottom: 18 }}>
                <h3 style={{ margin: 0, fontSize: "clamp(1.5rem, 3vw, 2.35rem)", fontWeight: 850 }}>
                  {locationName}
                </h3>
                <span className="ym-pill">{locationServices.length} services</span>
              </div>
              <div className="ym-grid-3">
                {locationServices.map((service, index) => (
                  <ServiceCard key={`${service._id}-${locationName}`} service={service} locationName={locationName} index={index} />
                ))}
              </div>
            </section>
          ))}

          {totalPages > 1 && (
            <div className="ym-actions" style={{ justifyContent: "center", marginTop: 42 }}>
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                <button
                  key={page}
                  className={`ym-button ${currentPage === page ? "ym-button-gold" : "ym-button-ghost"}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default GetStart;
